import { ApolloQueryResult, QueryResult, useQuery } from '@apollo/client';
import { capitalize, isEmpty, startCase } from 'lodash';
import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import toNumber from 'lodash/toNumber';
import React, { useEffect, useMemo, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Lozenge from '@atlaskit/lozenge';
import { ProgressTracker, Stages } from '@atlaskit/progress-tracker';
import SectionMessage from '@atlaskit/section-message';
import { AnalyticsTrackAction, AnalyticsTrackSubject, PlanType, analytics } from '@atlassian/temp-analytics';
import { Currency, OfferingStatus } from '@atlassian/cssxp-ccp-types';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import {
  BillingFrequency,
  BillingStatus,
  EntitlementDetails,
  FetchAvailableOfferingsForEntitlementQuery,
  FetchAvailablePricingPlansQuery,
  FetchAvailablePricingPlansQueryVariables,
  FetchAvailablePromotionsForEntitlementQuery,
  FetchAvailablePromotionsForEntitlementQueryVariables,
  FetchLinkedEntitlementsForTableQuery,
  FetchLinkedEntitlementsForTableQueryVariables,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  Offering,
  PricingPlan,
  Promotion,
  RelationshipPathType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { logTimeStamp } from '@atlassian/temp-metal';
import { getEntitlementDefaults, getUserTierForEntitlement, updateEntitlementPlan } from '@atlassian/temp-proxies';
import {
  EntitlementBaseProduct,
  EntitlementDefaultsRequest,
  EntitlementDefaultsResponse,
  OMSMetadata,
} from '@atlassian/cssxp-types-entitlement';
import {
  PatchOperation,
  PatchRequestOperation,
  PricingPlanType,
  PromotionInstance,
  UpdateOrderRequest,
} from '@atlassian/cssxp-typescommon';
import {
  EntitlementEdition,
  MARKETPLACE_PROMO_CODE,
  getParentContainerId,
  isFreePlanEntitlement,
  isOfferingArchived,
  isOfferingAtNotice,
  isOfferingPaid,
  notActivePricingStatus,
} from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_IMMEDIATE_DOWNGRADE_FOR_FREE_ENTITLEMENT,
  ENABLE_REMOVE_PROMOTION_API,
} from '@atlassian/cssxp-utils-generic';
import { isJCAProduct, isJSMProduct, isProductSkipTrialEligible, isStatuspage } from '@atlassian/cssxp-utils-product';
import { ApprovalType, EntityType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalFormSection, useApprovals } from '@css-xp/apd-process-manager';
import { TaskDetailAction, useCancelRequest, useTaskList } from '@css-xp/apd-task-manager';
import {
  EntitlementIcon,
  ErrorComponents,
  InflightInvoiceBanner,
  InfoPanel,
  InvoiceGroupSelectItem,
  NotificationTypes,
  SelectPromotionSelectItem,
  getGSACBaseURL,
  isDiscretionaryDiscountPromotion,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useModalDialog,
  useNotification,
  useSidebar,
  withApollo,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import {
  currentEntitlementType,
  fetchLinkedEntitlementsForTable,
  getChargeElement,
  getLinkedEntitlementsDetails,
  isValidURL,
} from '@css-xp/base/entitlement-utils';
import { isTrialEntitlement } from '../../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import { getFormError, validateBillTo } from '../../../../entitlement-page/src/components/entitlement-page-utils';
import { fetchAvailableOfferingsForEntitlement } from '../../../../entitlement-page/src/queries/fetch-available-offerings-for-entitlement';
import { fetchAvailablePricingPlans } from '../../../../entitlement-page/src/queries/fetch-available-pricing-plans';
import { fetchAvailablePromotionsForEntitlementQuery } from '../../../../entitlement-page/src/queries/fetch-available-promotions-for-entitlement';
import { getCustomPromotionInstance } from '../../utils/promotion-utils';
import { getPricingEligibility } from './manage-entitlement-helper';
import './manage-entitlement.scss';
import { ManagePlan } from './manage-plan';
import { ManagePromotion } from './manage-promotion';
import { DiscretionaryEstimate, ReviewChange } from './review-change';
import { buildMessage } from './utils/message-builder';

enum TrackerState {
  VISITED = 'visited',
  CURRENT = 'current',
  UNVISITED = 'unvisited',
}

enum FormTrackerStep {
  PLAN = 0,
  PROMOTIONS,
  REVIEW,
}

enum FormStages {
  PLAN = 'Plan',
  PROMOTIONS = 'Discounts',
  REVIEW = 'Review',
}

interface CustomValues {
  value: string;
  label: string;
}

interface DefaultEntitlementData {
  response?: {
    [key: string]: {
      [key: string]: EntitlementDefaultsResponse;
    };
  };
  error?: any;
  loading?: boolean;
}

interface LinkedEntitlementsWithUserTier {
  response?: {
    [key: string]: LinkedDestinationEntitlement[];
  };
  error?: any;
  loading?: boolean;
}

interface Props {
  entitlement: EntitlementDetails;
  transactionAccountContext: TransactionAccountContextType;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

export interface FormData {
  currentPlan?: string;
  newPlan?: {
    label: string;
    value: string;
  };
  units?: string;
  billingFrequency?: BillingFrequency;
  pricing?: string;
  promotionsToAdd?: SelectPromotionSelectItem[];
  promotionsToRemove?: SelectPromotionSelectItem[];
  schedulePromotionsToRemove?: SelectPromotionSelectItem[];
  invoiceGroupId?: InvoiceGroupSelectItem;
  supportTicketLink?: string;
  notes?: string;
  customDuration?: CustomValues[];
  customIterations?: string[];
  customReason?: CustomValues[];
  customValue?: string[];
  skipTrial?: boolean;
  comments?: string;
  pricePlan?: {
    label: string;
    value: string;
    chargeElement: string;
    chargeType?: string;
    pricingPlanType?: string;
  };
  pricePlanId?: string;
  nextBillingAnchorTimestamp?: number;
  promoCode?: string;
  pricePlanName?: string;
  userTier?: {
    label: string;
    value: string;
  };
  consentTaken?: boolean;
}

enum OrderType {
  AMENDMENT_ORDER = 'AMENDMENT_ORDER',
}

export const ManageEntitlement = withApollo(({ entitlement, transactionAccountContext, refetch }: Props) => {
  const { data, getTransactionAccount } = transactionAccountContext;
  const transactionAccount = getTransactionAccount(data);
  const availableInvoiceGroups = defaultTo(transactionAccount?.availableInvoiceGroups?.nodes, []) as InvoiceGroup[];
  const isStatusPage = entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
  const isTrial = isTrialEntitlement(entitlement, isStatusPage);
  const isCurrentEntitlementFree = !isOfferingPaid(entitlement);
  const [isEnabled] = useFeatureToggle();
  const enableImmediateDowngradeForFreeEntitlement = isEnabled(ENABLE_IMMEDIATE_DOWNGRADE_FOR_FREE_ENTITLEMENT);
  const [isUpgradeTrialScenario, setIsUpgradeTrialScenario] = React.useState<boolean>(false);
  const [grandTotal, setGrandTotal] = React.useState<number>();
  const [isMonthlyDisabled, setIsMonthlyDisabled] = React.useState<boolean>(false);
  const [isDisplayEndNoCostPremiumBanner, setIsDisplayEndNoCostPremiumBanner] = React.useState<boolean>(false);
  const isExistingAnnualPlanForJCA =
    entitlement?.billingFrequency === BillingFrequency.ANNUAL && !isStatuspage(entitlement?.productId);

  const approvalEval = React.useRef<any>();
  const [currentProgressIndex, setCurrentProgressIndex] = React.useState<number>(0);
  const [currFormState, setCurrFormState] = React.useState<FormData>({
    invoiceGroupId: {
      invoiceGroup: {},
      value: (entitlement?.billingGroupId || availableInvoiceGroups?.[0]?.id) as string,
    },
  });

  const [billingFrequency, setBillingFrequency] = React.useState<BillingFrequency>(
    defaultTo(
      currFormState?.billingFrequency,
      defaultTo(entitlement?.pricingPlan?.billingFrequency, BillingFrequency.MONTHLY)
    )
  );
  const [userTier, setUserTier] = React.useState<string | undefined>(
    defaultTo(currFormState?.userTier?.value, undefined)
  );
  const [availableOfferingsData, setAvailableOfferings] = React.useState<
    Partial<QueryResult<FetchAvailableOfferingsForEntitlementQuery>>
  >({ loading: true });
  const [availablePromotions, setAvailablePromotions] = React.useState<Promotion[]>(
    defaultTo(entitlement?.availablePromotions, []) as Promotion[]
  );
  const [cachedPromotions, setCachedPromotions] = React.useState<any>({});
  const [isSkipTrialChecked, setIsSkipTrialChecked] = React.useState(!isTrial || !!currFormState?.skipTrial);
  const [isSkipTrialDisabled, setIsSkipTrialDisabled] = React.useState(!(isExistingAnnualPlanForJCA && isTrial)); // make it default as true once we suport edition change for annual evergreen
  const [productSkipTrialEligible, setProductSkipTrialEligible] = React.useState(
    isProductSkipTrialEligible(defaultTo(entitlement?.offering?.productKey, ''), !!entitlement?.isApp)
  );
  const [customDiscount, setCustomDiscount] = React.useState<number>(0);
  const [isImmediateFlag, setIsImmediateFlag] = React.useState(false);
  const [selectedOfferingId, setSelectedOfferingId] = useState<string>();
  const [discretionaryEstimate, setDiscretionaryEstimate] = React.useState<Partial<DiscretionaryEstimate>>({
    loading: false,
  });
  // Billing Profile with missing payment method or ship to address are invalid .
  const [isBillingProfileInValid, setIsBillingProfileInValid] = React.useState<boolean>(false);
  const [defaultPricingPlan, evaluatePricingPlan] = React.useState<string>('');
  const [
    linkedEntitlementsWithUserTier,
    setLinkedEntitlementsWithUserTier,
  ] = React.useState<LinkedEntitlementsWithUserTier>({});
  const [defaultEntitlementData, setDefaultEntitlementData] = React.useState<Partial<DefaultEntitlementData>>({});
  const gsacBaseUrl = getGSACBaseURL();

  const currentSchedulePromotions = entitlement?.scheduledChanges?.promotions?.map((promotion, index) => {
    return {
      promotion: {
        ...promotion,
        index: index.toString(),
      },
    };
  }) as Promotion[];
  const activePromotions = entitlement?.promotions || [];

  const patchRemovePromotionEnabled = isEnabled(ENABLE_REMOVE_PROMOTION_API);
  const { error: formError, isProcessing, submitForm, complete, data: response } = useFormSubmit<
    UpdateOrderRequest,
    any
  >({
    submitFormAction: updateEntitlementPlan,
  });

  const currentInvoiceGroup = useMemo(() => {
    return ((transactionAccount?.availableInvoiceGroups?.nodes ?? []) as InvoiceGroup[])?.find(
      (invoiceGroup: InvoiceGroup) => {
        return invoiceGroup.id === entitlement.billingGroupId;
      }
    );
  }, [entitlement, transactionAccount]);

  const validateBillToAddress = useMemo(() => {
    return validateBillTo(transactionAccount);
  }, [transactionAccount]);

  const linkedEntsTableVisible = useMemo(() => {
    if (isStatuspage(entitlement?.productId)) {
      return false;
    }
    return !!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length;
  }, [entitlement]);

  const selectedBillingFrequency = useMemo(() => {
    return defaultTo(billingFrequency, defaultTo(entitlement?.pricingPlan?.billingFrequency, BillingFrequency.MONTHLY));
  }, [billingFrequency]);

  const { data: availablePromotionData, refetch: promotionsRefetch } = useQuery<
    FetchAvailablePromotionsForEntitlementQuery,
    FetchAvailablePromotionsForEntitlementQueryVariables
  >(fetchAvailablePromotionsForEntitlementQuery, {
    variables: { id: entitlement.entitlementNumber },
    fetchPolicy: 'cache-first',
  });

  const updateLinkedAppEntitlementsUserTiers = (
    linkedEntitlements?: LinkedDestinationEntitlement[]
  ): LinkedDestinationEntitlement[] => {
    if (linkedEntitlements?.length) {
      const linkedEntWithGreaterUserTierValue = linkedEntitlements.find(
        (linkedEntitlementObj) =>
          !linkedEntitlementObj?.isApp && toNumber(linkedEntitlementObj?.userTier ?? 0) > toNumber(userTier ?? 0)
      );
      return (
        linkedEntitlements?.map((linkedEntitlementObj: LinkedDestinationEntitlement) => {
          if (linkedEntitlementObj?.isApp) {
            if (!linkedEntWithGreaterUserTierValue) {
              linkedEntitlementObj.userTier = toNumber(userTier ?? 0);
            }
          }
          return linkedEntitlementObj;
        }) || []
      );
    }
    return [];
  };

  const onFetchLinkedEntitlementsCompleted = async (data?: FetchLinkedEntitlementsForTableQuery) => {
    if (isStatusPage) return;
    const linkedEntitlements = data?.entitlementDetails?.linkedEntitlements;
    if (selectedBillingFrequency === BillingFrequency.MONTHLY) {
      if (linkedEntitlements && isFreePlanEntitlement(entitlement)) {
        const hasAnnualLinkedEntitlement = linkedEntitlements.some(
          (linkedEnt) => linkedEnt?.billingFrequency === BillingFrequency.ANNUAL
        );
        if (hasAnnualLinkedEntitlement) {
          setBillingFrequency(BillingFrequency.ANNUAL);
          setIsMonthlyDisabled(true);
        }
      }
      return;
    }
    if (linkedEntitlements) {
      const filteredLinkedEntitlements =
        (linkedEntitlements.filter(
          (linkedEnt) =>
            !(
              (linkedEnt?.isSandbox && linkedEnt?.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX) ||
              linkedEnt?.entitlementId === getParentContainerId(entitlement)
            ) && linkedEnt?.billingStatus === BillingStatus.ACTIVE
        ) as Array<LinkedDestinationEntitlement>) || [];

      try {
        setLinkedEntitlementsWithUserTier({
          ...linkedEntitlementsWithUserTier,
          loading: true,
        });
        const fetchLinkedEntitlementsWithDefaultUserTier = await Promise.all(
          filteredLinkedEntitlements?.map(async (linkedEntitlementObj: LinkedDestinationEntitlement) => {
            const requestParams: EntitlementDefaultsRequest = {
              transactionAccountId: defaultTo(linkedEntitlementObj?.transactionAccountId, ''),
              offeringId: defaultTo(linkedEntitlementObj?.offeringId, ''),
              billingCycle: selectedBillingFrequency,
            };
            if (linkedEntitlementObj?.isApp) {
              requestParams.relationshipType = RelationshipPathType.APP_DEPENDENCE;
              const appDependenceEntitlement = defaultTo(linkedEntitlementObj?.relatesFromEntitlements, []).filter(
                (relations) => relations?.relationshipType === RelationshipPathType.APP_DEPENDENCE
              );
              requestParams.relatedEntitlementId = defaultTo(appDependenceEntitlement?.[0]?.entitlementId, undefined);
            } else {
              requestParams.currentEntitlementId = linkedEntitlementObj?.entitlementId;
            }
            const response = await getEntitlementDefaults(requestParams);
            const chargeQuantity = response?.chargeQuantities?.[0]?.quantity;
            const pricingPlanId = response?.pricingPlanId?.value;
            const chargeElement = response?.chargeQuantities?.[0]?.chargeElement;
            linkedEntitlementObj.pricingPlanId = pricingPlanId;
            linkedEntitlementObj.chargeElement = chargeElement;
            linkedEntitlementObj.userTier = chargeQuantity;

            if (pricingPlanId && !isNil(chargeQuantity)) {
              linkedEntitlementObj.userTier = await getUserTierForEntitlement({ pricingPlanId, chargeQuantity });
            }

            return linkedEntitlementObj;
          }) || []
        );

        setLinkedEntitlementsWithUserTier({
          ...linkedEntitlementsWithUserTier,
          loading: false,
          response: {
            ...(linkedEntitlementsWithUserTier?.response || {}),
            [selectedBillingFrequency]: fetchLinkedEntitlementsWithDefaultUserTier,
          },
        });
      } catch (error) {
        setLinkedEntitlementsWithUserTier({
          ...linkedEntitlementsWithUserTier,
          loading: false,
          error, // TODO: handle this error case
        });
      }
    }
  };

  const { data: linkedEntitlementDetails, loading: isLinkedentitlementDetailsLoading, error } = useQuery<
    FetchLinkedEntitlementsForTableQuery,
    FetchLinkedEntitlementsForTableQueryVariables
  >(fetchLinkedEntitlementsForTable, {
    variables: {
      id: entitlement.id,
      searchLinkedEntitlementParams: {
        entitlementId: entitlement.id,
        containerId: getParentContainerId(entitlement),
        productType: entitlement.productType,
        currentEntitlementType: currentEntitlementType(entitlement),
        destinationEntitlementType: [
          LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
          LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
          LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
          LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
        ],
        findApps: true,
        findProducts: true,
        checkForLastParentExistence: false,
      },
    },
    displayName: 'fetchAllLinkedEntitlements',
    notifyOnNetworkStatusChange: true,
    onCompleted: onFetchLinkedEntitlementsCompleted,
  });

  const filteredLinkedEntitlement = linkedEntitlementDetails?.entitlementDetails?.linkedEntitlements
    ? linkedEntitlementDetails?.entitlementDetails?.linkedEntitlements?.filter(
        (entitlement) => entitlement?.billingGroupId
      )
    : [];

  const linkedEntitlementInvoiceGroupId =
    filteredLinkedEntitlement.length > 0 ? filteredLinkedEntitlement[0]?.billingGroupId : null;

  const [availableFilteredInvoiceGroups, defaultInvoiceGroup] = useMemo(() => {
    const filteredInvoiceGroups = linkedEntitlementInvoiceGroupId
      ? availableInvoiceGroups.filter((invoiceGroup) => invoiceGroup.id === linkedEntitlementInvoiceGroupId)
      : availableInvoiceGroups;
    return [
      filteredInvoiceGroups,
      linkedEntitlementInvoiceGroupId ? filteredInvoiceGroups[0] : currentInvoiceGroup || null,
    ];
  }, [linkedEntitlementInvoiceGroupId, availableInvoiceGroups]);

  const pricingEligibility = useMemo(() => {
    return getPricingEligibility({
      invoiceGroup: defaultInvoiceGroup,
      transactionAccount,
    });
  }, [defaultInvoiceGroup]);

  const filterOptions = [
    {
      key: 'currency',
      value: defaultTo(currentInvoiceGroup?.currency, Currency.USD),
    },
    {
      key: 'offeringId',
      value: entitlement?.offeringId,
    },
    {
      key: 'billingCycle',
      value: entitlement?.pricingPlan?.billingFrequency || BillingFrequency.MONTHLY,
    },
  ];

  if (pricingEligibility) {
    filterOptions.push({
      key: 'planType',
      value: pricingEligibility,
    });
  }

  const { data: availablePricingPlans, refetch: pricingPlansRefetch, loading: isFetchingPricingPlans } = useQuery<
    FetchAvailablePricingPlansQuery,
    FetchAvailablePricingPlansQueryVariables
  >(fetchAvailablePricingPlans, {
    variables: {
      id: entitlement.entitlementNumber,
      filterOptions,
    },
    fetchPolicy: 'cache-first',
  });

  const isBillingCycleChanged = useMemo(() => {
    if (
      (entitlement?.pricingPlan?.billingFrequency === BillingFrequency.MONTHLY || isCurrentEntitlementFree) &&
      billingFrequency === BillingFrequency.ANNUAL &&
      !isStatusPage
    ) {
      if (!isCurrentEntitlementFree) {
        setSelectedOfferingId('');
        setIsSkipTrialChecked(true);
        setIsSkipTrialDisabled(true);
      }

      return true;
    }
    return false;
  }, [billingFrequency]);

  const defaultApiOfferingId = selectedOfferingId || entitlement?.offeringId || '';

  const getDefaultEntitlementData = async () => {
    if (entitlement) {
      try {
        setDefaultEntitlementData({
          ...defaultEntitlementData,
          loading: true,
        });
        const requestParams: EntitlementDefaultsRequest = {
          transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
          offeringId: defaultApiOfferingId,
          billingCycle: selectedBillingFrequency,
        };
        if (entitlement?.baseProduct?.toLowerCase().includes('statuspage') && entitlement?.parentSen) {
          // is Statuspage add on, then pass the parent statuspage
          requestParams.currentEntitlementId = entitlement?.parentSen;
        } else if (entitlement?.isApp) {
          const appDependenceEntitlement = defaultTo(entitlement?.relatesFromEntitlements, []).filter(
            (relations) => relations?.relationshipType === RelationshipPathType.APP_DEPENDENCE
          );
          // is an JCA app, then pass the app parent JCA subscription
          requestParams.relatedEntitlementId = defaultTo(appDependenceEntitlement?.[0]?.entitlementId, undefined);
          requestParams.relationshipType = RelationshipPathType.APP_DEPENDENCE;
        } else {
          requestParams.currentEntitlementId = entitlement?.id;
        }
        const response = await getEntitlementDefaults(requestParams);
        setDefaultEntitlementData({
          ...defaultEntitlementData,
          loading: false,
          response: {
            ...(defaultEntitlementData?.response || {}),
            [selectedBillingFrequency]: {
              ...(defaultEntitlementData?.response?.[selectedBillingFrequency] || {}),
              [defaultApiOfferingId]: response,
            },
          },
        });
        updateFormState({
          ...currFormState,
          nextBillingAnchorTimestamp: defaultTo(get(response, 'billingAnchorTime.value'), undefined),
        });
      } catch (error) {
        setDefaultEntitlementData({
          ...defaultEntitlementData,
          loading: false,
          error, // TODO: handle this error case
        });
      }
    }
  };

  useEffect(() => {
    if (!defaultEntitlementData?.response?.[selectedBillingFrequency]?.[defaultApiOfferingId]) {
      getDefaultEntitlementData();
    }
  }, [selectedBillingFrequency, selectedOfferingId]);

  useEffect(() => {
    if (!linkedEntitlementsWithUserTier?.response?.[selectedBillingFrequency]) {
      onFetchLinkedEntitlementsCompleted(linkedEntitlementDetails);
    }
  }, [selectedBillingFrequency]);

  useEffect(() => {
    if (
      !isEmpty(linkedEntitlementsWithUserTier?.response?.[selectedBillingFrequency]) &&
      !linkedEntitlementsWithUserTier?.loading
    ) {
      const linkedEntitlementsWithAppUserTiers = updateLinkedAppEntitlementsUserTiers(
        linkedEntitlementsWithUserTier?.response?.[selectedBillingFrequency]
      );
      setLinkedEntitlementsWithUserTier({
        ...linkedEntitlementsWithUserTier,
        response: {
          ...linkedEntitlementsWithUserTier.response,
          [selectedBillingFrequency]: linkedEntitlementsWithAppUserTiers,
        },
      });
    }
  }, [userTier, linkedEntitlementsWithUserTier?.loading]);

  const checkIsOptingForUpgradeTrial = () => {
    const selectedOffering = availableOfferings.find((offering) => offering.id === selectedOfferingId);
    const currentOffering = entitlement.offering;

    // Case when no offering selected and is in upgrade trial then isUpgradeTrialScenario should be true .
    if (!selectedOfferingId && entitlement.upgradeTrial) {
      setIsUpgradeTrialScenario(true);
      setIsSkipTrialChecked(false);
      return;
    }

    // Case when offering selected and is in upgrade trial then isUpgradeTrialScenario should be false .
    if (selectedOffering?.name.toUpperCase() === EntitlementEdition.PREMIUM && !!entitlement.upgradeTrial) {
      setIsUpgradeTrialScenario(false);
      setIsSkipTrialChecked(true);
      return;
    }
    // Case when PREMIUM offering selected without skipping trial and is in not upgrade trial then isUpgradeTrialScenario should be true .
    if (
      !isStatusPage &&
      currentOffering?.name.toUpperCase() === EntitlementEdition.STANDARD &&
      !isTrial &&
      selectedOffering?.name.toUpperCase() === EntitlementEdition.PREMIUM &&
      !isSkipTrialChecked
    ) {
      setIsUpgradeTrialScenario(true);
    } else {
      setIsUpgradeTrialScenario(false);
    }
  };

  React.useEffect(() => {
    checkIsOptingForUpgradeTrial();
  }, [selectedOfferingId, isSkipTrialChecked]);

  React.useEffect(() => {
    (async () => {
      const offerings = await graphqlClient().query<FetchAvailableOfferingsForEntitlementQuery>({
        query: fetchAvailableOfferingsForEntitlement,
        variables: { id: entitlement.id },
        fetchPolicy: 'no-cache',
      });
      setAvailableOfferings({ ...offerings });
    })();
  }, []);

  React.useEffect(() => {
    if (availablePromotionData?.entitlementDetails?.availablePromotions) {
      setAvailablePromotions(
        defaultTo(availablePromotionData?.entitlementDetails?.availablePromotions, []) as Promotion[]
      );
    }
  }, [availablePromotionData]);

  React.useEffect(() => {
    (async () => {
      if (!isSkipTrialDisabled) {
        const intendedBillingPeriodState = isSkipTrialChecked ? 'PAID' : 'TRIAL';
        if (!cachedPromotions[intendedBillingPeriodState]) {
          const fetchedPromotions = await promotionsRefetch({
            intendedBillingPeriodState,
          });
          setCachedPromotions((prevState: any) => {
            return {
              ...prevState,
              [intendedBillingPeriodState]: fetchedPromotions?.data,
            };
          });
        } else {
          setAvailablePromotions(cachedPromotions[intendedBillingPeriodState]?.entitlementDetails?.availablePromotions);
        }
      }
    })();
  }, [isSkipTrialDisabled, isSkipTrialChecked, cachedPromotions, promotionsRefetch]);

  const availablePricings = defaultTo(
    availablePricingPlans?.entitlementDetails?.availablePricingPlans,
    []
  ) as PricingPlan[];

  useEffect(() => {
    const filterOptions = [
      {
        key: 'currency',
        value: defaultTo(currentInvoiceGroup?.currency, Currency.USD),
      },
      {
        key: 'offeringId',
        value: selectedOfferingId || entitlement?.offeringId,
      },
      {
        key: 'billingCycle',
        value: billingFrequency,
      },
    ];
    if (pricingEligibility) {
      filterOptions.push({
        key: 'planType',
        value: pricingEligibility,
      });
    }
    pricingPlansRefetch({
      filterOptions,
    });
  }, [pricingPlansRefetch, currentInvoiceGroup, entitlement, billingFrequency, selectedOfferingId, pricingEligibility]);

  const updateFormState = (updatedFormState: FormData) => {
    setCurrFormState(cloneDeep(assign(currFormState, updatedFormState)));
  };

  const { openModalDialog } = useModalDialog();
  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const { closeSidebar } = useSidebar();

  const onApprovalComplete = React.useCallback(() => {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('approval-submit-success')
      .withHeader('Approval request submitted')
      .withMessage('You can view your request in the Approval requests section.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch?.();
  }, [closeSidebar, refetch, sendSuccessNotification, setNotificationSent]);

  const { isDiscountApprovalFlowEnabled, isPricingPlanChangeApprovalFlowEnabled } = useApprovalDeskPermissions();

  const {
    cancelApprovalRequestError,
    cancelApprovalRequestProcessing,
    submitRequestAfterApprovalCancelation,
  } = useCancelRequest({
    businessKey: entitlement.entitlementNumber,
  });

  const { hasOpenApprovalRequest, error: taskListError, data: taskListData } = useTaskList({
    businessKey: entitlement.entitlementNumber,
    skip: !entitlement.entitlementNumber || !isDiscountApprovalFlowEnabled || !isPricingPlanChangeApprovalFlowEnabled,
  });

  const {
    approvalRequired: isApprovalRequiredForDiscount,
    approvalError: approvalErrorForDiscount,
    approvalList: approvalListForDiscount,
    approvalProcessing: approvalProcessingForDiscount,
    evaluate: evaluateApprovalForDiscount,
    setApprovalRequired: setApprovalRequiredDiscount,
    approvalSlackChannel: approvalSlackChannelForDiscount,
    evaluateApprovalProcessing: evaluateDiscountApprovalProcessing,
  } = useApprovals({
    approvalType: ApprovalType.ORDER_DISCOUNT,
    onApprovalComplete,
  });

  const {
    approvalRequired: isApprovalRequiredForPricing,
    approvalError: approvalErrorForPricing,
    approvalList: approvalListForPricing,
    approvalProcessing: approvalProcessingForPricing,
    evaluate: evaluateApprovalForPricing,
    setApprovalRequired: setApprovalRequiredPricing,
    submitApproval,
    approvalSlackChannel: approvalSlackChannelForPricing,
    evaluateApprovalProcessing: evaluatePricingPlanApprovalProcessing,
  } = useApprovals({
    approvalType: ApprovalType.PRICING_PLAN_CHANGE,
    onApprovalComplete,
  });

  const filterValidPromotion = (selectedPromo: SelectPromotionSelectItem) => {
    const promotion = selectedPromo?.promotion;
    return !!promotion?.id;
  };

  const getPlanChangeType = (
    selectedOfferingId: string | undefined,
    availableOfferings: Array<any>,
    entitlement: EntitlementDetails
  ) => {
    const selectedOffering = availableOfferings?.find((offering: Offering) => offering?.id === selectedOfferingId);
    const offering = entitlement?.offering as Offering;
    if (!offering?.level || !selectedOffering?.level || offering?.level === selectedOffering?.level) return null;
    if (selectedOffering?.level > offering?.level) {
      return PlanType.UPGRADE;
    }
    return PlanType.DOWNGRADE;
  };

  // To determine if changed offering is FREE and have pricing type FREE .
  const isImmediateAmendmentToFreeOrder = (availableOfferings: Offering[], selectedOfferingId: string): boolean => {
    return !!availableOfferings.find((offering) => {
      return (
        offering.pricingType === 'FREE' &&
        offering.id === selectedOfferingId &&
        offering.name.toLowerCase().includes('free')
      );
    });
  };

  const requestLinkedEntitlements = (offeringId?: string) => {
    const data = linkedEntitlementsWithUserTier?.response?.[selectedBillingFrequency] || [];
    if (data.length === 0) {
      return [];
    }

    return getLinkedEntitlementsDetails(
      entitlement,
      data.filter(
        (linkedEntitlementObj) => linkedEntitlementObj?.subscriptionId && linkedEntitlementObj?.pricingPlanId
      ),
      offeringId
    );
  };

  const buildRequest = (formData: FormData) => {
    let scheduleRemovePromotions: any = [];
    let request: UpdateOrderRequest = {
      isBillingCycleChanged,
      entitlementId: entitlement.id,
      billingFrequency: currFormState?.billingFrequency,
      units: currFormState?.userTier?.value || currFormState?.units || '1',
      isPromotionStackingAllowed: true,
      invoiceGroupId: defaultTo(formData?.invoiceGroupId?.value, ''),
      offeringId: defaultTo(currFormState?.newPlan?.value, entitlement?.offering?.id),
      orderType: OrderType.AMENDMENT_ORDER,
      productId: entitlement.productId || '', //@ts-ignore
      chargeQuantities: entitlement?.scheduledChanges?.chargeQuantities || [],
      pricingPlanType: defaultPricingPlan,
      chargeElement: getChargeElement(entitlement?.productId as string),
      linkedEntitlements: requestLinkedEntitlements(currFormState?.newPlan?.value),
      // Indicates an upgradeTrial order . Backend OMS engine utilizes this flag to identify if scheduled promotions should be added or not .
      isUpgradeTrialOrder: isUpgradeTrialScenario || !!entitlement.upgradeTrial,
      isImmediateAmendmentToFree:
        enableImmediateDowngradeForFreeEntitlement &&
        isImmediateAmendmentToFreeOrder(availableOfferings, selectedOfferingId as string),
    };

    // Indicates user is moving to PAID premium in this case premium pricingPlan ID should be send .
    if (!isUpgradeTrialScenario && entitlement.upgradeTrial) {
      request.pricingPlanId = entitlement.upgradeTrial.pricingPlan?.id;
      request.chargeType = entitlement.upgradeTrial.pricingPlan?.chargeType as string;
    }
    if (currFormState?.pricePlan) {
      request.pricingPlanId = currFormState?.pricePlan?.value;
      request.chargeElement = currFormState?.pricePlan?.chargeElement;
      request.chargeType = currFormState?.pricePlan?.chargeType;
    }
    if (currFormState?.nextBillingAnchorTimestamp) {
      request.nextBillingAnchorTimestamp = `${currFormState?.nextBillingAnchorTimestamp}`;
    }

    //To check that the pricing plan status while amending an order
    request.isExpiredPricingPlan = notActivePricingStatus.includes(entitlement?.pricingPlan?.status as string);

    const scheduledPromotionOperations: PatchRequestOperation[] = [];
    request.promotionsToRemove = [];
    if (patchRemovePromotionEnabled) {
      if (
        currFormState &&
        currFormState?.promotionsToRemove &&
        currFormState?.promotionsToRemove?.length > 0 &&
        currentSchedulePromotions &&
        currentSchedulePromotions?.length > 0
      ) {
        //@ts-ignore
        currFormState.schedulePromotionsToRemove = currentSchedulePromotions;
        //@ts-ignore
        (currFormState?.promotionsToRemove || []).forEach((promo: any, index: number) => {
          scheduledPromotionOperations.push({
            op: PatchOperation.REMOVE,
            path: `/subscription/promotionInstances/${index}`,
          });
        });
      }

      let ind = -1;
      (currFormState?.schedulePromotionsToRemove || []).forEach((promo: any) => {
        if (
          activePromotions.some((activepromotion: any, index: number) => {
            ind = index;
            return activepromotion?.id === promo.promotion.id;
          })
        ) {
          scheduledPromotionOperations.push({
            op: PatchOperation.REMOVE,
            path: `/subscription/promotionInstances/${ind}`,
          });
        }
        scheduledPromotionOperations.push({
          op: PatchOperation.REMOVE,
          path: `/subscription/scheduledChanges/promotionInstances/${promo.promotion.index}`,
        });
        request.promotionsToRemove?.push(promo.promotion.promotionInstanceId);
      });

      request.operations = [...scheduledPromotionOperations];
    } else {
      scheduleRemovePromotions = (currFormState?.schedulePromotionsToRemove || []).map((promotion: any) => {
        request.promotionsToRemove?.push(promotion.promotion.promotionInstanceId);
        return {
          promotionDefinition: {
            promotionId: promotion.promotion.promotionInstanceId,
          },
        };
      });
    }

    const promotions = Object.keys(currFormState).filter((key) => key.trim().includes('promotionId'));
    const promotionsToAdd: any[] = [];

    promotions?.forEach((key: string, index: number) => {
      let promotionToAdd;

      if (!isDiscretionaryDiscountPromotion(currFormState, key)) {
        promotionToAdd = {
          promotionInstanceId: null,
          promotionDefinition: {
            promotionId: null,
            promotionCode: null,
            customisedValues: null,
          },
        };

        if (get(currFormState, key)?.promotion?.type === MARKETPLACE_PROMO_CODE) {
          //@ts-ignore
          promotionToAdd.promotionDefinition.promotionCode = currFormState.promoCode;
        } else {
          promotionToAdd.promotionDefinition.promotionId = get(currFormState, `${key}.value`, '');
        }
      } else {
        promotionToAdd = getCustomPromotionInstance(index, currFormState);
      }

      promotionToAdd && promotionsToAdd.push(promotionToAdd);
    });
    const filteredPromotionsToAdd = promotionsToAdd.filter(
      (promotionToAdd) =>
        promotionToAdd.promotionDefinition.promotionId ||
        promotionToAdd.promotionDefinition.promotionCode ||
        promotionToAdd.promotionDefinition.customisedValues
    ); //@ts-ignore
    let removePromotions: PromotionInstance[] = defaultTo(currFormState?.promotionsToRemove, [])
      .filter(filterValidPromotion)
      .map((promotionItem) => ({
        promotionDefinition: {
          promotionId: defaultTo(promotionItem?.promotion?.id, undefined),
          customisedValues: promotionItem?.promotion?.isPromotionCustomisable
            ? {
                applicationReason: {
                  id: defaultTo(promotionItem?.promotion?.applicationReason?.id, ''),
                },
                benefits: [
                  {
                    value: defaultTo(promotionItem?.promotion?.benefits?.[0]?.value, undefined),
                    duration: defaultTo(promotionItem?.promotion?.benefits?.[0]?.duration, ''),
                    iterations: defaultTo(promotionItem?.promotion?.benefits?.[0]?.iterations, null),
                  },
                ],
              }
            : null,
        },
      }));
    if (scheduleRemovePromotions.length > 0) {
      removePromotions = removePromotions.concat(scheduleRemovePromotions);
    }

    request = {
      ...request,
      removePromotions,
      addPromotions: filteredPromotionsToAdd,
      metadata: {
        [OMSMetadata.SUPPORT_TICKET_NUMBER]: formData?.supportTicketLink
          ? isValidURL(formData.supportTicketLink)
            ? formData.supportTicketLink
            : `${gsacBaseUrl}/browse/${formData.supportTicketLink}`
          : null,
        [OMSMetadata.HAND_NOTE]: defaultTo(formData?.notes, null),
        [OMSMetadata.CONSENT_TAKEN]: defaultTo(formData?.consentTaken?.toString(), null),
      },
    };
    if (
      (isExistingAnnualPlanForJCA && isTrial) || //remove this condition once we start supporting edition change for annual evergreen
      isBillingCycleChanged ||
      (productSkipTrialEligible && (!isNil(currFormState.newPlan) || !isNil(currFormState.pricePlan)))
    ) {
      request = {
        ...request,
        skipTrial: isSkipTrialChecked || !!currFormState?.skipTrial,
      };
    }
    return request;
  };

  const discreteDiscountPromotions = React.useMemo(() => {
    const promotions = [];
    const promotionKeys = Object.keys(currFormState).filter((key) => key.trim().includes('promotionId'));
    for (let i = 0; i < promotionKeys.length; i++) {
      if (isDiscretionaryDiscountPromotion(currFormState, promotionKeys[i])) {
        promotions.push(getCustomPromotionInstance(i, currFormState));
      }
    }
    return promotions;
  }, [currFormState]);

  const calculateAndSendTimeMetric = () => {
    logTimeStamp(new Date().getTime(), 'Change Subscription', SignalFxFEPerfStore.FE_USER_TASK_CHANGE_SUB_TIME);
  };

  const buildApprovalEntityInformation = (approvalType: ApprovalType, toPricingPlanType = '') => {
    const defaultEntity = {
      entityType: EntityType.ENTITLEMENT,
      entityId: entitlement.id,
      entityNumber: entitlement.entitlementNumber,
      discountPercentage: customDiscount,
      discountValue: discretionaryEstimate?.discretionaryDiscountAmount ?? 0,
    };
    return [
      {
        ...defaultEntity,
        ...(approvalType === ApprovalType.PRICING_PLAN_CHANGE
          ? {
              fromPricingPlanKey: entitlement?.pricingPlan?.pricingPlanType ?? '',
              toPricingPlanKey: toPricingPlanType,
            }
          : {}),
      },
    ];
  };

  const actualApprovalType = useMemo(() => {
    return isApprovalRequiredForPricing ? ApprovalType.PRICING_PLAN_CHANGE : ApprovalType.ORDER_DISCOUNT;
  }, [isApprovalRequiredForDiscount, isApprovalRequiredForPricing]);

  const approvalRequiredForTypes = useMemo(() => {
    return isApprovalRequiredForPricing && isApprovalRequiredForDiscount
      ? [ApprovalType.ORDER_DISCOUNT, ApprovalType.PRICING_PLAN_CHANGE]
      : [];
  }, [isApprovalRequiredForDiscount, isApprovalRequiredForPricing]);

  const approvalList = useMemo(() => {
    if (isApprovalRequiredForDiscount && isApprovalRequiredForPricing) {
      return [
        ...approvalListForDiscount.filter((item) => item.approvalRequired),
        ...approvalListForPricing.filter((item) => item.approvalRequired),
      ];
    }
    if (isApprovalRequiredForDiscount) {
      return approvalListForDiscount.filter((item) => item.approvalRequired);
    }
    if (isApprovalRequiredForPricing) {
      return approvalListForPricing.filter((item) => item.approvalRequired);
    }
    return [];
  }, [approvalListForDiscount, approvalListForPricing, isApprovalRequiredForDiscount, isApprovalRequiredForPricing]);

  const handleFormSubmit = async (formData: FormData) => {
    setIsBillingProfileInValid(false);

    if (currentProgressIndex < items.length - 1) {
      setCurrFormState(cloneDeep(assign(currFormState, formData)));
      setCurrentProgressIndex(currentProgressIndex + 1);
    } else {
      calculateAndSendTimeMetric();

      if (
        (grandTotal ?? 0) > 0 &&
        (!validateBillToAddress || !defaultTo(formData?.invoiceGroupId?.invoiceGroup?.defaultPaymentMethod, null))
      ) {
        setIsBillingProfileInValid(true);
        return;
      }
      const request = buildRequest(formData);
      if (isApprovalRequiredForDiscount || isApprovalRequiredForPricing) {
        submitRequestAfterApprovalCancelation(
          () =>
            submitApproval({
              approvalType: actualApprovalType,
              businessKey: entitlement.entitlementNumber as string,
              entityInformation: buildApprovalEntityInformation(
                actualApprovalType,
                formData?.pricePlan?.pricingPlanType
              ),
              gsacId: formData.supportTicketLink,
              initiatorComments: formData.comments,
              additionalInformation: request,
            }),
          hasOpenApprovalRequest
        );
      } else {
        submitRequestAfterApprovalCancelation(() => submitForm(request), hasOpenApprovalRequest);
      }
    }
  };

  const handleCustomPromotionValueChange = React.useCallback(({ discountPercentage }) => {
    // as only one discretionary discount is allowed, considering the last one in the stack
    setCustomDiscount(discountPercentage);
  }, []);

  const isApprovalFormRequired = React.useCallback(() => {
    return (
      (isApprovalRequiredForPricing || isApprovalRequiredForDiscount) &&
      !discretionaryEstimate?.loading &&
      !(discretionaryEstimate?.error && discreteDiscountPromotions.length) &&
      !discretionaryEstimate?.isDisabled &&
      currentProgressIndex === FormTrackerStep.REVIEW
    );
  }, [isApprovalRequiredForPricing, isApprovalRequiredForDiscount, discretionaryEstimate, currentProgressIndex]);

  const isPricingEligibilityMismatching = useMemo(() => {
    if (currentProgressIndex === FormTrackerStep.REVIEW) {
      if (currFormState?.invoiceGroupId?.value) {
        const invoiceGroup = availableInvoiceGroups.find((ig) => ig?.id === currFormState?.invoiceGroupId?.value);
        const shipToPriceEligibility = invoiceGroup?.shipToPriceEligibility;
        if (shipToPriceEligibility) {
          const entPricingPlanType = availablePricings.find(
            (pricingPlan) => pricingPlan?.id === (currFormState?.pricePlan?.value || currFormState?.pricePlanId)
          )?.type;
          if (entPricingPlanType && entPricingPlanType !== shipToPriceEligibility.toUpperCase()) return true;
        }
      }
    }
    return false;
  }, [currentProgressIndex, currFormState, availableInvoiceGroups]);

  const isSubmitButtonDisabled = React.useCallback(() => {
    return (
      isPricingEligibilityMismatching ||
      (isCurrentEntitlementFree && !selectedOfferingId && isBillingCycleChanged) ||
      availableOfferingsData.loading ||
      defaultEntitlementData.loading ||
      !!taskListError ||
      evaluateDiscountApprovalProcessing ||
      evaluatePricingPlanApprovalProcessing ||
      discretionaryEstimate.loading ||
      (!!discretionaryEstimate?.error && !!discreteDiscountPromotions.length) ||
      discretionaryEstimate?.isDisabled ||
      !!approvalErrorForPricing ||
      isLinkedentitlementDetailsLoading
    );
  }, [
    isPricingEligibilityMismatching,
    availableOfferingsData.loading,
    taskListError,
    evaluateDiscountApprovalProcessing,
    evaluatePricingPlanApprovalProcessing,
    discretionaryEstimate,
    defaultEntitlementData.loading,
    selectedOfferingId,
    isBillingCycleChanged,
    isLinkedentitlementDetailsLoading,
  ]);

  let availableOfferings = useMemo(() => {
    const allAvailableOfferings = availableOfferingsData?.data?.entitlementDetails?.availableOfferings || [];
    return allAvailableOfferings.filter((offering) => {
      const offeringName = offering?.name?.toLowerCase() || '';

      return !offeringName.includes('sandbox');
    });
  }, [availableOfferingsData?.data?.entitlementDetails?.availableOfferings]) as Offering[];

  if (complete && !notificationSent) {
    const isStatuspageProduct = isStatuspage(entitlement?.productId);
    const isTrialProductBeforeOrderSubmit = isTrialEntitlement(entitlement, isStatuspageProduct);
    const isTrialToTrialConversion = !currFormState?.skipTrial && isTrialProductBeforeOrderSubmit;
    const notificationConfig = buildMessage({
      response,
      isApprovalRequired:
        approvalEval.current &&
        (approvalEval.current?.discountApproval?.isApprovalRequired ||
          approvalEval.current?.pricePlanChangeApproval?.isApprovalRequired),
      isTrialToTrialConversion,
      hasOptedForUpgradeTrial: isUpgradeTrialScenario,
    });
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('change-subscription-success')
      .withHeader(notificationConfig.header)
      .withMessage(notificationConfig.message)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    const selectedOfferingId = defaultTo(currFormState?.newPlan?.value, entitlement?.offering?.id);
    analytics.sendTrackEvent({
      attributes: {
        changeType: getPlanChangeType(selectedOfferingId, availableOfferings, entitlement),
      },
      action: AnalyticsTrackAction.UPDATED,
      actionSubject: AnalyticsTrackSubject.CHANGE_PLAN,
    });
    closeSidebar();
    refetch();
  }

  const stages = Object.values(FormStages);

  const items: Stages = stages.map((stage, index) => ({
    id: `${index}`,
    label: stage,
    percentageComplete: currentProgressIndex - 1 < index ? 0 : 100,
    status:
      currentProgressIndex > index
        ? TrackerState.VISITED
        : currentProgressIndex === index
        ? TrackerState.CURRENT
        : TrackerState.UNVISITED,
    onClick: () => setCurrentProgressIndex(index),
  }));

  const currentOffering = availableOfferingsData?.data?.entitlementDetails?.offering;
  if (isTrial && currentOffering) {
    availableOfferings = [...availableOfferings, currentOffering];
  }

  const renderFormStep = ({ entitlement }: { entitlement: EntitlementDetails }) => {
    switch (currentProgressIndex) {
      case FormTrackerStep.PLAN: {
        if (availableOfferingsData.loading || isLinkedentitlementDetailsLoading) return <LoadingIndicator />;
        return (
          <ManagePlan
            availablePricingPlans={availablePricings}
            availableOfferings={availableOfferings}
            formState={currFormState}
            entitlement={entitlement}
            transactionAccountContext={transactionAccountContext}
            isSkipTrialChecked={isSkipTrialChecked}
            isSkipTrialDisabled={isSkipTrialDisabled}
            onSkipTrialChange={setIsSkipTrialChecked}
            onSkipTrialDisableChange={setIsSkipTrialDisabled}
            productSkipTrialEligible={productSkipTrialEligible}
            setProductSkipTrialEligible={setProductSkipTrialEligible}
            isImmediateFlag={isImmediateFlag}
            onIsImmediateFlagChange={setIsImmediateFlag}
            evaluatePricingPlan={evaluatePricingPlan}
            onSelectOfferingId={setSelectedOfferingId}
            selectedOfferingId={selectedOfferingId}
            linkedEntsTableVisible={linkedEntsTableVisible}
            linkedEntitlementsData={linkedEntitlementDetails}
            linkedEntitlementsLoading={isLinkedentitlementDetailsLoading || linkedEntitlementsWithUserTier.loading}
            defaultEntitlementData={
              defaultEntitlementData?.response?.[selectedBillingFrequency]?.[defaultApiOfferingId]
            }
            isDefaultEntitlementDataLoading={defaultEntitlementData.loading || isFetchingPricingPlans}
            linkedEntitlementsError={error}
            parentContainerID={getParentContainerId(entitlement)}
            productType={entitlement.productType}
            setBillingFrequency={setBillingFrequency}
            setUserTier={setUserTier}
            billingFrequency={billingFrequency}
            isDisplayEndNoCostPremiumBanner={setIsDisplayEndNoCostPremiumBanner}
            isUpgradeTrialScenario={isUpgradeTrialScenario}
            isBillingCycleChanged={isBillingCycleChanged}
            isCurrentEntitlementFree={isCurrentEntitlementFree}
            isExistingAnnualPlanForJCA={isExistingAnnualPlanForJCA}
            isMonthlyDisabled={isMonthlyDisabled}
          />
        );
      }
      case FormTrackerStep.PROMOTIONS:
        return (
          <ManagePromotion
            availableOfferings={availableOfferings}
            formState={currFormState}
            entitlement={entitlement}
            availablePromotions={availablePromotions}
            onCustomPromotionValueChange={handleCustomPromotionValueChange}
            isUpgradeTrialScenario={isUpgradeTrialScenario}
            isOfferingChanged={!!selectedOfferingId && selectedOfferingId !== entitlement?.offeringId}
          />
        );
      case FormTrackerStep.REVIEW: {
        return (
          <ReviewChange
            availableOfferings={availableOfferings}
            availableInvoiceGroups={availableFilteredInvoiceGroups}
            formState={currFormState}
            entitlement={entitlement}
            isSkipTrialChecked={isSkipTrialChecked}
            isImmediateFlag={isImmediateFlag}
            availablePricingPlans={availablePricings}
            updateFormState={updateFormState}
            onDiscretionaryEstimateChange={setDiscretionaryEstimate}
            linkedEntitlementInvoiceGroupId={linkedEntitlementInvoiceGroupId}
            linkedEntitlementsData={linkedEntitlementsWithUserTier?.response?.[selectedBillingFrequency]}
            defaultEntitlementData={
              defaultEntitlementData?.response?.[selectedBillingFrequency]?.[defaultApiOfferingId]
            }
            grandTotal={grandTotal}
            setGrandTotal={setGrandTotal}
            parentContainerID={getParentContainerId(entitlement)}
            productType={entitlement.productType}
            isBillingCycleChanged={isBillingCycleChanged}
            isUpgradeTrialScenario={isUpgradeTrialScenario}
            isExistingAnnualPlanForJCA={isExistingAnnualPlanForJCA}
            isCurrentEntitlementFree={isCurrentEntitlementFree}
            isOfferingChanged={!!selectedOfferingId && selectedOfferingId !== entitlement?.offeringId}
          />
        );
      }
    }
  };

  React.useEffect(() => {
    if (currentProgressIndex === FormTrackerStep.REVIEW) {
      (async () => {
        if (typeof discretionaryEstimate?.discretionaryDiscountAmount === 'number') {
          if (isDiscountApprovalFlowEnabled && discreteDiscountPromotions.length) {
            const discountApproval: any = await evaluateApprovalForDiscount(
              buildApprovalEntityInformation(ApprovalType.ORDER_DISCOUNT)
            );
            approvalEval.current = { discountApproval };
          }
        }
      })();
    } else {
      setDiscretionaryEstimate({ loading: false });
      setApprovalRequiredDiscount(false);
      setApprovalRequiredPricing(false);
    }
  }, [
    currentProgressIndex,
    discreteDiscountPromotions,
    discretionaryEstimate?.discretionaryDiscountAmount,
    isDiscountApprovalFlowEnabled,
  ]);

  React.useEffect(() => {
    if (currentProgressIndex === FormTrackerStep.REVIEW) {
      (async () => {
        if (isPricingPlanChangeApprovalFlowEnabled && currFormState?.pricePlan?.pricingPlanType) {
          const pricePlanChangeApproval: any = await evaluateApprovalForPricing(
            buildApprovalEntityInformation(ApprovalType.PRICING_PLAN_CHANGE, currFormState?.pricePlan?.pricingPlanType)
          );
          approvalEval.current = { pricePlanChangeApproval };
        }
      })();
    }
  }, [currentProgressIndex, currFormState?.pricePlan?.pricingPlanType, isPricingPlanChangeApprovalFlowEnabled]);

  const pricingType = entitlement?.pricingPlan?.type + ' ' + 'pricing';

  const hasPromotions = () => {
    if (entitlement.promotions?.length || entitlement.scheduledChanges?.promotions?.length) {
      return true;
    }
    return false;
  };

  const [isAppArchivedWarningShown, isAppAtNoticeWarningShown] = React.useMemo(() => {
    if (!entitlement?.isApp) return [false, false];
    if (selectedOfferingId) {
      if (selectedOfferingId === entitlement?.offeringId) {
        return [isOfferingArchived(entitlement), isOfferingAtNotice(entitlement)];
      }
      const selectedOffering = availableOfferings?.find((offering: Offering) => offering?.id === selectedOfferingId);
      return [
        selectedOffering?.status === OfferingStatus.EXPIRED,
        selectedOffering?.status === OfferingStatus.AT_NOTICE,
      ];
    }
    return [isOfferingArchived(entitlement), isOfferingAtNotice(entitlement)];
  }, [selectedOfferingId, entitlement]);

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <form {...formProps}>
          <div className="header">
            <FormHeader title="Change subscription">
              <EntitlementIcon
                logoId={entitlement?.logoId as string}
                productId={entitlement?.productId as string}
                isCloud={true}
                className="avatar medium"
              />{' '}
              <h4 style={{ display: 'inline' }}>{`${defaultTo(entitlement?.productName, 'Unnamed product')} ${
                entitlement?.entitlementNumber
              }`}</h4>
              &nbsp;&nbsp;
              {entitlement?.scheduledChanges !== null && <Lozenge appearance="inprogress">changes scheduled</Lozenge>}
              &nbsp;&nbsp;
              {entitlement.pricingPlan?.type && (
                <Lozenge appearance="default">{capitalize(startCase(pricingType ?? ''))}</Lozenge>
              )}
            </FormHeader>
          </div>
          {currentProgressIndex === FormTrackerStep.PROMOTIONS && isUpgradeTrialScenario && (
            <FormSection>
              {hasPromotions() ? (
                <SectionMessage appearance="warning" testId="upgrade-trial-with-discount-banner">
                  <p>No discounts can be added or removed to the base plan during an ongoing trial.</p>
                </SectionMessage>
              ) : (
                <SectionMessage appearance="warning" testId="upgrade-trial-without-discount-banner">
                  <p>No discounts can be added during an ongoing trial.</p>
                </SectionMessage>
              )}
            </FormSection>
          )}
          {isDisplayEndNoCostPremiumBanner && (
            <FormSection>
              <SectionMessage appearance="warning" testId="end-no-cost-premium-banner">
                <p>
                  Performing this action will end the no-cost Premium trial and the plan will be upgraded immediately.
                  The customer will be charged pro rata till the next bill date.
                </p>
              </SectionMessage>
            </FormSection>
          )}
          {isPricingEligibilityMismatching && (
            <FormSection>
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel message="There is a mismatch of price eligibility of billing profile and one of the entitlements. Please correct the price eligibility for either entitlements or billing profile" />
                }
              />
            </FormSection>
          )}
          <FormSection>
            {!!taskListError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the discount promotion for the following reason"
                    message={taskListError.message}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>
            {isAppArchivedWarningShown && (
              <InfoPanel
                type="warning"
                title="This app has been archived"
                message="The subscription can only be moved to an active offering."
              />
            )}
          </FormSection>
          <FormSection>
            {isAppAtNoticeWarningShown && (
              <InfoPanel
                type="warning"
                title="This offering is in the process of being archived"
                message="Keep in mind that any changes will only apply until the subscription end date, unless a new plan is selected."
              />
            )}
          </FormSection>
          <FormSection>
            {isDiscountApprovalFlowEnabled && !!discreteDiscountPromotions.length && !!discretionaryEstimate?.error && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the bill estimation for the following reason"
                    message={discretionaryEstimate?.error?.message}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>
            {!!cancelApprovalRequestError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the discount promotion for the following reason"
                    message={cancelApprovalRequestError.message}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>
            {hasOpenApprovalRequest && (
              <InfoPanel
                type="warning"
                message={
                  <div className="approval-request-inflight-banner">
                    One or more approval requests are currently in progress. If you perform this action, those approval
                    requests will be canceled automatically.
                    {taskListData?.taskList?.tasks?.length && (
                      <TaskDetailAction
                        task={taskListData.taskList.tasks[0]}
                        text="View approval request details"
                        refetch={refetch}
                        refetchAll={refetch}
                      />
                    )}
                  </div>
                }
              />
            )}
          </FormSection>
          <FormSection>
            {isBillingProfileInValid && (
              <SectionMessage
                appearance="error"
                title={
                  !validateBillToAddress && !currentInvoiceGroup?.defaultPaymentMethod
                    ? 'Update billing account and billing profile'
                    : !validateBillToAddress
                    ? 'Update billing account'
                    : 'Update billing profile'
                }
                testId="invalid-billing-profile"
              >
                <div>
                  <p>Subscription changes can’t be made because there’s:</p>
                  <br />
                  <ul>
                    {!validateBillToAddress ? <li>No bill-to address available for the billing account</li> : null}

                    {!currentInvoiceGroup?.defaultPaymentMethod ? (
                      <li>No payment method available for the billing profile</li>
                    ) : null}
                  </ul>
                  <br />
                  <p>
                    {`Add the information${validateBillToAddress ? ' or switch the billing profile' : ''}`}, then try
                    again. Still having trouble? Contact the <strong>#help-advocate-central</strong> channel.
                  </p>
                </div>
              </SectionMessage>
            )}
          </FormSection>

          <FormSection>
            <InflightInvoiceBanner
              transactionAccountId={entitlement.transactionAccountId as string}
              message={'An invoice is currently being generated. Any changes you make now will appear on this invoice.'}
            />
          </FormSection>

          <div className="progress-content">
            <ProgressTracker items={items} spacing="compact" />
          </div>

          <FormSection>
            {formError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to create transaction account for the following reason"
                    message={getFormError(formError, OrderType.AMENDMENT_ORDER)}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>
            {!!approvalErrorForDiscount && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the discount promotion for the following reason"
                    message={approvalErrorForDiscount.message}
                  />
                }
              />
            )}
            {!!approvalErrorForPricing && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the pricing plan change for the following reason"
                    message={approvalErrorForPricing.message}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>{renderFormStep({ entitlement })}</FormSection>
          {isApprovalFormRequired() ? (
            <ApprovalFormSection
              approvalType={actualApprovalType}
              approvalRequiredProcesses={approvalRequiredForTypes}
              slackChannel={
                (isApprovalRequiredForPricing ? approvalSlackChannelForPricing : approvalSlackChannelForDiscount) ?? ''
              }
              onCancel={() => closeSidebar()}
              isLoading={
                (approvalProcessingForDiscount && !evaluateDiscountApprovalProcessing) ||
                cancelApprovalRequestProcessing ||
                (approvalProcessingForPricing && !evaluatePricingPlanApprovalProcessing)
              }
              approvalItems={approvalList}
              isDisabled={evaluateDiscountApprovalProcessing || evaluatePricingPlanApprovalProcessing}
            />
          ) : (
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Cancel
                </Button>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isDisabled={isSubmitButtonDisabled()}
                  isLoading={isProcessing || cancelApprovalRequestProcessing}
                  testId="save-button"
                >
                  {currentProgressIndex === FormTrackerStep.REVIEW ? 'Submit' : 'Continue'}
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          )}
        </form>
      )}
    </Form>
  );
});
